import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import AddFieldOnStep from '../../../../components/Pipelines/Modals/AddFieldOnStep'
import AddStepInPipeline from '../../../../components/Pipelines/Modals/AddStepInPipeline'
import ViewOrEditTicket from '../../../../components/Pipelines/Modals/ViewOrEditTicket'
import ViewOrEditTags from '../../../../components/Pipelines/Modals/ViewOrEditTags'
import FieldTypeSelector from '../../../../components/Pipelines/Modals/FieldTypeSelector'
import AddIntegratedFieldModal from '../../../../components/Pipelines/Modals/AddIntegratedFieldModal'
import { coverUrlManager } from '../../../../utils/coverUrlManager'
import FillRequiredFieldsModal from '../../../../components/Pipelines/Modals/FillRequiredFieldsModal'
import TagComponent from '../../../../components/ui/Tag'
import styled from 'styled-components'
import { useDrag, useDrop } from 'react-dnd'
import { Step, Ticket } from '../types'
import { Pipeline, StepColumn, StepHeader, TicketItem, TicketList, CardTitle, MetaData, HorizontalDiv, AddInStep, InputForAdd, AddStep, TitleWithActions, ActionGroup, IconButton, CardMetaRow, CardMetaItem } from '../style'
import { CircleAlertIcon, MessageSquareText, Plus, User, X, SquarePen, Trash2, Check, Calendar, CheckSquare, Paperclip, MoreHorizontal, Eye, Copy, Link2, Archive, Settings, Pencil } from 'lucide-react'
import axios from '../../../../config/axios'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePipelineContext } from '../PipelineContext'
import TableView from '../TableView'

type DragItem = { id: number; stepId: number; index: number }
type TicketOrderCache = Record<number, Ticket[]>

const CardActionButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6B7280;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    color: #111827;
  }
`

interface CardActionMenuProps {
  top?: number
  left?: number
  direction?: 'up' | 'down'
}

const CardActionMenu = styled.div<CardActionMenuProps>`
  position: fixed;
  top: ${({ top }) => (typeof top === 'number' ? `${top}px` : 'unset')};
  left: ${({ left }) => (typeof left === 'number' ? `${left}px` : 'unset')};
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 2147483647;
  min-width: 160px;
  padding: 8px 0;
  animation: ${({ direction }) => direction === 'up' ? 'slideUp' : 'slideDown'} 0.18s cubic-bezier(.4,0,.2,1);

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`

const CardActionMenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: ${props => props.$danger ? '#DC2626' : '#303030'};
  text-align: left;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.$danger ? '#FEF2F2' : '#f5f5f5'};
  }

  svg {
    color: ${props => props.$danger ? '#DC2626' : '#666666'};
    flex-shrink: 0;
  }
`

const StepSettingsButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #0090d3;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 144, 211, 0.1);
    color: #0077b3;
  }
`

const StepSettingsMenu = styled.div<{ top?: number; left?: number }>`
  position: fixed;
  top: ${({ top }) => (typeof top === 'number' ? `${top}px` : 'unset')};
  left: ${({ left }) => (typeof left === 'number' ? `${left}px` : 'unset')};
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 2147483647;
  min-width: 200px;
  padding: 8px 0;
  animation: slideDown 0.18s cubic-bezier(.4,0,.2,1);

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`

const StepSettingsMenuItem = styled.div<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: ${props => props.$danger ? '#DC2626' : '#303030'};
  text-align: left;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.$danger ? '#FEF2F2' : '#f5f5f5'};
  }

  svg {
    color: ${props => props.$danger ? '#DC2626' : '#666666'};
    flex-shrink: 0;
  }
`

const StepMenuItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const StepMenuDivider = styled.div`
  height: 1px;
  background-color: #e5e5e5;
  margin: 8px 0;
`

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.3s;
    border-radius: 20px;
  }

  span:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }

  input:checked + span {
    background-color: #0090D3;
  }

  input:checked + span:before {
    transform: translateX(16px);
  }
`

const DraggableTicket = ({
  ticket,
  index,
  onOpen,
  onEditClick,
  isEditing,
  titleDraft,
  onChangeTitle,
  onConfirmEdit,
  onCancelEdit,
  onDelete,
  onDuplicate,
  onCopyLink,
  onArchive,
  showArchived,
}: {
  ticket: Ticket
  index: number
  onOpen: () => void
  onEditClick: () => void
  isEditing: boolean
  titleDraft: string
  onChangeTitle: (value: string) => void
  onConfirmEdit: () => void
  onCancelEdit: () => void
  onDelete: () => void
  onDuplicate: () => void
  onCopyLink: () => void
  onArchive: () => void
  showArchived: boolean
}) => {
  const ref = useRef<HTMLLIElement | null>(null)
  const menuButtonRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; direction: 'up' | 'down' }>({ top: 0, left: 0, direction: 'down' })

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        if (menuButtonRef.current && menuButtonRef.current.contains(e.target as Node)) return
        setMenuOpen(false)
      }
    }
    const handleScrollOrResize = () => setMenuOpen(false)
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [])

  const handleToggleMenu = () => {
    if (menuOpen) {
      setMenuOpen(false)
      return
    }
    if (menuButtonRef.current) {
      const buttonRect = menuButtonRef.current.getBoundingClientRect()
      const menuHeight = 250
      const menuWidth = 160
      const buttonCenterX = buttonRect.left + (buttonRect.width / 2)
      let left = buttonCenterX - (menuWidth / 2)
      if (left + menuWidth > window.innerWidth) left = window.innerWidth - menuWidth - 10
      if (left < 10) left = 10
      let direction: 'up' | 'down' = 'down'
      let top = buttonRect.bottom + 5
      if (top + menuHeight > window.innerHeight) {
        direction = 'up'
        top = buttonRect.top - menuHeight - 5
      }
      setMenuPosition({ top, left, direction })
    }
    setMenuOpen(true)
  }

  const [coverImage, setCoverImage] = useState<string | undefined>();

  const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean}>({
    type: 'ticket',
    item: { id: ticket.id, stepId: ticket.stepId, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(ref)


  useEffect(() => {
    const updateCoverUrl = async () => {
      if (ticket.coverFile?.fileUrl && ticket.coverFile.id) {
        try {
          const url = await coverUrlManager.getCoverUrl(ticket.coverFile.id, ticket.coverFile.fileUrl);
          setCoverImage(url);
        } catch (error) {
          console.error('Erro ao obter URL da capa:', error);
          setCoverImage(ticket.coverFile.fileUrl);
        }
      } else {
        setCoverImage(undefined);
      }
    };

    updateCoverUrl();
  }, [ticket.coverFile]);

  const formatDeadline = (dateStr?: string) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).replace('.', '')
  }

  const isDeadlineExpired = (dateStr?: string) => {
    if (!dateStr) return false
    const deadline = new Date(dateStr)
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    deadline.setHours(0, 0, 0, 0)
    return deadline < now
  }

  const completedTasks = ticket.tasks?.filter(t => t.completed).length || 0
  const totalTasks = ticket.tasks?.length || 0
  const attachmentsCount = ticket.fileInTickets?.length || 0
  const commentsCount = ticket.comments?.length || 0

  return (
    <TicketItem
      ref={ref}
      onDoubleClick={onOpen}
      style={{ 
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move'
      }}
    >
      {coverImage && (
        <div style={{ width: '100%', height: '120px', marginBottom: '10px', overflow: 'hidden', borderRadius: '4px 4px 0 0' }}>
          <img 
            src={coverImage} 
            alt="Capa" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          />
        </div>
      )}
      <div style={coverImage ? { padding: '0 12px' } : {}}>
        {ticket.urgency && (
          <HorizontalDiv>
            <MetaData color='#BF383B' fontSize='10px'><CircleAlertIcon size={16} /> URGENTE</MetaData>
          </HorizontalDiv>
        )}
      </div>
      <TitleWithActions>
        {isEditing ? (
          <InputForAdd
            value={titleDraft}
            placeholder='Título do card'
            onChange={(e) => onChangeTitle(e.target.value)}
            onKeyDown={(e) => {
              if(e.key === 'Enter') onConfirmEdit()
              if(e.key === 'Escape') onCancelEdit()
            }}
            autoFocus
          />
        ) : (
          <CardTitle>{ticket.title}</CardTitle>
        )}
        {isEditing ? (
          <ActionGroup>
            <IconButton onClick={onConfirmEdit} onDoubleClick={(e) => e.stopPropagation()}><Check size={16} /></IconButton>
            <IconButton onClick={onCancelEdit} onDoubleClick={(e) => e.stopPropagation()}><X size={16} /></IconButton>
          </ActionGroup>
        ) : (
          <ActionGroup>
            <CardActionButton
              ref={menuButtonRef}
              onClick={(e) => { e.stopPropagation(); handleToggleMenu() }}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal size={18} />
            </CardActionButton>
          </ActionGroup>
        )}
      </TitleWithActions>
      {menuOpen && ReactDOM.createPortal(
        <CardActionMenu
          ref={menuRef}
          top={menuPosition.top}
          left={menuPosition.left}
          direction={menuPosition.direction}
        >
          <CardActionMenuItem onClick={() => { onOpen(); setMenuOpen(false) }}>
            <Eye size={16} /> Visualizar
          </CardActionMenuItem>
          <CardActionMenuItem onClick={() => { onDuplicate(); setMenuOpen(false) }}>
            <Copy size={16} /> Duplicar
          </CardActionMenuItem>
          <CardActionMenuItem onClick={() => { onEditClick(); setMenuOpen(false) }}>
            <SquarePen size={16} /> Renomear
          </CardActionMenuItem>
          <CardActionMenuItem onClick={() => { onCopyLink(); setMenuOpen(false) }}>
            <Link2 size={16} /> Copiar link
          </CardActionMenuItem>
          <CardActionMenuItem onClick={() => { onArchive(); setMenuOpen(false) }}>
            <Archive size={16} /> {showArchived ? 'Desarquivar' : 'Arquivar'}
          </CardActionMenuItem>
          <CardActionMenuItem $danger onClick={() => { onDelete(); setMenuOpen(false) }}>
            <Trash2 size={16} /> Excluir
          </CardActionMenuItem>
        </CardActionMenu>,
        document.body
      )}
      {ticket.createBy && (
        <HorizontalDiv>
          <MetaData>
            <div style={{width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#0090D3', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              {ticket.createBy.picture ? <img src={ticket.createBy.picture} alt={ticket.createBy.name} style={{width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover'}} /> : <User size={14} />}
            </div>
            <span>{ticket.createBy.name}</span>
          </MetaData>
        </HorizontalDiv>
      )}
      <CardMetaRow>
        {ticket.deadline && (
          <CardMetaItem 
            color={isDeadlineExpired(ticket.deadline) ? '#BF383B' : undefined}
            style={isDeadlineExpired(ticket.deadline) ? { fontWeight: 700 } : undefined}
          >
            <Calendar size={14} />
            {formatDeadline(ticket.deadline)}
          </CardMetaItem>
        )}
        {totalTasks > 0 && (
          <CardMetaItem>
            <CheckSquare size={14} />
            {completedTasks}/{totalTasks}
          </CardMetaItem>
        )}
        {commentsCount > 0 && (
          <CardMetaItem>
            <MessageSquareText size={14} />
            {commentsCount}
          </CardMetaItem>
        )}
        {attachmentsCount > 0 && (
          <CardMetaItem>
            <Paperclip size={14} />
            {attachmentsCount}
          </CardMetaItem>
        )}
      </CardMetaRow>
      {ticket.tags && ticket.tags.length > 0 &&
        <HorizontalDiv>
          {ticket.tags.filter(tag => tag.tag).map(tag => <TagComponent name={tag.tag.name} color={tag.tag.color} key={tag.tag.id} />)}
        </HorizontalDiv>
      }
    </TicketItem>
  )
}

const DroppableStepColumn = ({ step, children, onTicketMove, onStepMove }: { 
  step: Step, 
  children: React.ReactNode,
  onTicketMove: (ticketId: number, newStepId: number) => void,
  onStepMove: (stepId: number, targetStepId: number) => void
}) => {
  const ref = useRef<HTMLDivElement | null>(null)

  const [{ isOver }, drop] = useDrop({
    accept: ['ticket', 'step'],
    drop: (item: { id: number, stepId?: number }, monitor) => {
      const itemType = monitor.getItemType()
      if (itemType === 'ticket' && item.stepId !== step.id) {
        onTicketMove(item.id, step.id)
      }
      if (itemType === 'step' && item.id !== step.id) {
        onStepMove(item.id, step.id)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  })

  const [{ isDraggingStep }, drag] = useDrag({
    type: 'step',
    item: { id: step.id },
    collect: (monitor) => ({
      isDraggingStep: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  return (
    <StepColumn 
      ref={ref}
      style={{ 
        border: isOver ? '2px solid rgba(0, 144, 211, 0.3)' : 'none',
        opacity: isDraggingStep ? 0.6 : 1,
      }}
    >
      {children}
    </StepColumn>
  )
}

type FieldModalType = 
  | { mode: 'selector', stepId: number }
  | { mode: 'custom', stepId: number, field?: any }
  | { mode: 'integrated', stepId: number }
  | null

const Kanban = () => {
  const { isConfiguringPipeline, viewOrEditTagsModalIsOpen, setViewOrEditTagsModalIsOpen, viewMode, sortBy, filters, setAvailableUsers, setAvailableTags, showArchived, isAddingTableCard, setIsAddingTableCard } = usePipelineContext()
  const [ fieldModal, setFieldModal ] = useState<FieldModalType>(null)
  const [ fieldsInSteps, setFieldsInSteps ] = useState<{pipelineField:any, step:any, isRequired?: boolean}[]>([])
  const [ addStepModalIsOpen, setAddStepModalIsOpen ] = useState<boolean>(false)
  const [ requiredFieldsModal, setRequiredFieldsModal ] = useState<{ ticketId: number, targetStepId: number } | null>(null)
  const navigate = useNavigate()
  const [ steps, setSteps ] = useState<Step[]>([])
  const [ tickets, setTickets ] = useState<Ticket[]>([])
  const [ allTags, setAllTags ] = useState<{id: number, name: string, color?: string}[]>([])
  const [ newTicketIn, setNewTicketIn ] = useState<number | null>(null)
  const [ newTicketTitle, setNewTicketTitle ] = useState<string>('')
  const [ creatingTicket, setCreatingTicket ] = useState<boolean>(false)
  const [ editingTicketId, setEditingTicketId ] = useState<number | null>(null)
  const [ ticketTitleDraft, setTicketTitleDraft ] = useState<string>('')
  const [ editingFieldId, setEditingFieldId ] = useState<number | null>(null)
  const [ fieldNameDraft, setFieldNameDraft ] = useState<string>('')
  const [ editingStepId, setEditingStepId ] = useState<number | null>(null)
  const [ stepNameDraft, setStepNameDraft ] = useState<string>('')
  const [ typeMap, setTypeMap ] = useState<Record<number, string>>({})
  const [ searchParams ] = useSearchParams()
  const ticketOrderRef = useRef<TicketOrderCache>({})
  const pipelineId = searchParams.get('pipelineId')
  const [ stepMenuOpen, setStepMenuOpen ] = useState<number | null>(null)
  const [ stepMenuPosition, setStepMenuPosition ] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const stepMenuButtonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({})
  const stepMenuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const uniqueUsers = tickets
      .filter(t => t.createBy)
      .map(t => t.createBy!)
      .filter((user, index, self) => self.findIndex(u => u.id === user.id) === index)
    
    console.debug('[Kanban] Available users for filter:', uniqueUsers)
    setAvailableUsers(uniqueUsers)
  }, [tickets, setAvailableUsers])

  useEffect(() => {
    console.debug('[Kanban] Available tags for filter:', allTags)
    setAvailableTags(allTags)
  }, [allTags, setAvailableTags])

  const filteredAndSortedTickets = useMemo(() => {
    let result = [...tickets]

    console.debug('[Kanban] Filtering tickets, initial count:', result.length)
    console.debug('[Kanban] Active filters:', filters)
    console.debug('[Kanban] Sort by:', sortBy)

    if (filters.keyword) {
      const keyword = filters.keyword.toLowerCase()
      result = result.filter(t => t.title.toLowerCase().includes(keyword))
    }

    if (filters.urgency) {
      result = result.filter(t => t.urgency)
    }

    const now = new Date()
    const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000)

    if (filters.dueDate.notExpired || filters.dueDate.nearExpiry || filters.dueDate.expired) {
      result = result.filter(t => {
        if (!t.deadline) return false
        const deadline = new Date(t.deadline)
        
        if (filters.dueDate.notExpired && deadline > now) return true
        if (filters.dueDate.nearExpiry && deadline > now && deadline <= threeDaysFromNow) return true
        if (filters.dueDate.expired && deadline < now) return true
        return false
      })
    }

    if (filters.assignedUsers.length > 0) {
      result = result.filter(t => t.createBy && filters.assignedUsers.includes(t.createBy.id))
    }

    if (filters.tags.length > 0) {
      result = result.filter(t => (t.tags ?? []).some(tag => filters.tags.includes(tag.tag.id)))
    }

    switch (sortBy) {
      case 'phase':
        result.sort((a, b) => {
          const stepA = steps.find(s => s.id === a.stepId)
          const stepB = steps.find(s => s.id === b.stepId)
          const nameA = stepA?.name || ''
          const nameB = stepB?.name || ''
          return nameA.localeCompare(nameB)
        })
        break
      case 'name_asc':
        result.sort((a, b) => a.title.localeCompare(b.title))
        break
      case 'name_desc':
        result.sort((a, b) => b.title.localeCompare(a.title))
        break
      case 'recent':
        result.sort((a, b) => b.id - a.id)
        break
      case 'oldest':
        result.sort((a, b) => a.id - b.id)
        break
      case 'urgency':
        result.sort((a, b) => (b.urgency ? 1 : 0) - (a.urgency ? 1 : 0))
        break
      case 'deadline':
        result.sort((a, b) => {
          if (!a.deadline && !b.deadline) return 0
          if (!a.deadline) return 1
          if (!b.deadline) return -1
          return new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
        })
        break
    }

    console.debug('[Kanban] Filtered tickets count:', result.length)

    return result
  }, [tickets, filters, sortBy, steps])

  const fetchFieldsInStepsData = useCallback(async () => {
    if(!pipelineId) return
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/fields?pipelineId=${pipelineId}`)
      if(response.data) {
        const normalized = response.data.data.map((f: any) => ({
          pipelineField: f.pipelineField ?? f.PipelineField,
          step: f.step ?? f.Step,
          isRequired: f.isRequired,
        }))
        setFieldsInSteps(normalized)
      }
    } catch (error) {
      console.error(error)
    }
  }, [pipelineId])

  useEffect(() => {
    const fetchFieldTypes = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/types`)
        if(response.data?.data) {
          const map: Record<number, string> = {}
          response.data.data.forEach((item: {id:number, name:string}) => { map[item.id] = item.name })
          setTypeMap(map)
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchFieldTypes()
  }, [])

  const fetchStepsAndTickets = useCallback(async () => {
    if(!pipelineId) return
    try {
      const [responseSteps, responseTickets, responseTags] = await Promise.all([
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step?pipelineId=${pipelineId}`),
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket?pipelineId=${pipelineId}&archived=${showArchived}`),
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/tag`)
      ])
      if(responseSteps.data.data) setSteps(responseSteps.data.data)
      if(responseTickets.data.data) {
        const normalized = responseTickets.data.data.map((t: any) => {
          const mapped = { ...t };
          if (!mapped.coverFile && mapped.GeneralFile) {
            mapped.coverFile = {
              id: mapped.GeneralFile.id,
              fileUrl: mapped.GeneralFile.fileUrl,
              title: mapped.GeneralFile.title,
              originalName: mapped.GeneralFile.originalName,
              fileName: mapped.GeneralFile.fileName,
              fileSize: mapped.GeneralFile.fileSize,
              mimeType: mapped.GeneralFile.mimeType
            }
          }
          mapped.comments = mapped.Comment || mapped.comments || mapped.comments === undefined ? (mapped.Comment ? mapped.Comment.map((c: any) => ({ id: c.id })) : (mapped.comments || [])) : mapped.comments
          const rawTags = mapped.TagTicket || mapped.tags || []
          mapped.tags = rawTags.map((t: any) => {
            if (t.tag) return t
            if (t.Tag) return { ...t, tag: t.Tag }
            return t
          })
          mapped.tasks = mapped.TicketTask || mapped.tasks || []
          mapped.fileInTickets = mapped.FileInTicket || mapped.fileInTickets || []
          if (mapped.FieldInTicket) {
            mapped.fieldInTickets = mapped.FieldInTicket.map((fit: any) => ({
              pipelineFieldId: fit.pipelineFieldId,
              stringValue: fit.stringValue,
              bigStringValue: fit.bigStringValue,
              integerValue: fit.integerValue,
              decimalValue: fit.decimalValue,
              dateTimeValue: fit.dateTimeValue,
              booleanValue: fit.booleanValue,
              pipelineField: {
                id: fit.PipelineField?.id,
                name: fit.PipelineField?.name,
                typeFieldId: fit.PipelineField?.typeFieldId,
                typeField: fit.PipelineField?.TypeField,
                integrationConfig: fit.PipelineField?.integrationConfig,
              },
              isRequired: false,
            }))
          }
          return mapped
        })
        setTickets(normalized)
      }
      if(responseTags.data?.data) {
        console.debug('[Kanban] Fetched tags:', responseTags.data.data)
        setAllTags(responseTags.data.data)
      }
    } catch (error) {
      console.error(error)
    }
  }, [pipelineId, showArchived])

  const saveStepOrder = useCallback(async (orderedSteps: Step[]) => {
    if(!pipelineId) return
    try {
      await Promise.all(
        orderedSteps.map((step, idx) =>
          axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${step.id}` , {
            position: idx,
          })
        )
      )
    } catch (error) {
      console.error(error)
    }
  }, [pipelineId])

  const saveTicketOrder = useCallback(async (stepId: number, ticketsInStep: Ticket[]) => {
    try {
      await Promise.all(
        ticketsInStep.map((ticket) =>
          axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticket.id}`, {
            stepId,
          })
        )
      )
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    if(!isConfiguringPipeline) {
      setEditingStepId(null)
      setStepNameDraft('')
    }

    if(isConfiguringPipeline) {
      fetchFieldsInStepsData()
    }
  }, [isConfiguringPipeline, fetchFieldsInStepsData])

  useEffect(() => {
    fetchStepsAndTickets()
  }, [fetchStepsAndTickets])

  useEffect(() => {
    if(!addStepModalIsOpen) {
      fetchStepsAndTickets()
    }
  }, [addStepModalIsOpen, fetchStepsAndTickets])

  const createTicket = async () => {
    if(creatingTicket) return
    const trimmed = newTicketTitle.trim()
    if(!trimmed || newTicketIn === null) {
      setNewTicketIn(null)
      setNewTicketTitle('')
      return
    }
    try {
      setCreatingTicket(true)
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket`, {
        title: trimmed,
        stepId: newTicketIn
      })
      if(response.data) {
        setTickets(prev => [...prev, {...response.data.data, comments:[], tags:[]}])
      }
    } catch (error) {
      console.error(error)
    } finally {
      setCreatingTicket(false)
      setNewTicketIn(null)
      setNewTicketTitle('')
    }
  }

  const handleCreateTicket = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if(e.key === 'Enter') {
      await createTicket()
    }
    if(e.key === 'Escape') {
      setNewTicketIn(null)
      setNewTicketTitle('')
    }
  }

  const handleUpdateStep = async (stepId: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if(e.key === 'Enter') {
      await saveStepName(stepId)
    }
    if(e.key === 'Escape') {
      cancelEditStep()
    }
  }

  const saveStepName = async (stepId: number) => {
    const newName = stepNameDraft.trim()
    if(!newName) return
    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}`, {
        name: newName
      })
      if(response.data) {
        setSteps(prev => prev.map(step => step.id === stepId ? response.data.data : step))
        setEditingStepId(null)
        setStepNameDraft('')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const cancelEditStep = () => {
    setEditingStepId(null)
    setStepNameDraft('')
  }

  const handleDeleteStep = async (stepId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}`)
      setSteps(prev => prev.filter(step => step.id !== stepId))
      setTickets(prev => prev.filter(ticket => ticket.stepId !== stepId))
      setFieldsInSteps(prev => prev.filter(field => field.step.id !== stepId))
      if(editingStepId === stepId) {
        cancelEditStep()
      }
      setStepMenuOpen(null)
    } catch (error) {
      console.error(error)
    }
  }

  const handleDuplicateStep = async (stepId: number) => {
    try {
      const step = steps.find(s => s.id === stepId)
      if (!step) return


      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step`, {
        name: `${step.name} (cópia)`,
        pipelineId: Number(pipelineId)
      })

      if (response.data) {
        const newStep = response.data.data
        setSteps(prev => [...prev, newStep])


        const ticketsToDuplicate = tickets.filter(t => t.stepId === stepId)
        for (const ticket of ticketsToDuplicate) {
          const duplicateResponse = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticket.id}/duplicate`, {
            stepId: newStep.id
          })
          if (duplicateResponse.data) {
            setTickets(prev => [...prev, duplicateResponse.data.data])
          }
        }
      }
      setStepMenuOpen(null)
    } catch (error) {
      console.error(error)
    }
  }

  const handleToggleStepMenu = (stepId: number) => {
    if (stepMenuOpen === stepId) {
      setStepMenuOpen(null)
      return
    }

    const button = stepMenuButtonRefs.current[stepId]
    if (button) {
      const rect = button.getBoundingClientRect()
      setStepMenuPosition({ top: rect.bottom + 5, left: rect.left })
    }
    setStepMenuOpen(stepId)
  }

  const handleToggleStepLock = async (stepId: number) => {
    const step = steps.find(s => s.id === stepId)
    if (!step) return
    
    const newValue = !(step as any).allowCardCreation
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}`, {
        allowCardCreation: newValue
      })
      setSteps(prev => prev.map(s => s.id === stepId ? { ...s, allowCardCreation: newValue } : s))
    } catch (error) {
      console.error('Erro ao atualizar configuração da etapa:', error)
    }
  }

  const handleStartRenameStep = (stepId: number) => {
    const step = steps.find(s => s.id === stepId)
    if (step) {
      setEditingStepId(stepId)
      setStepNameDraft(step.name)
    }
    setStepMenuOpen(null)
  }


  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (stepMenuRef.current && !stepMenuRef.current.contains(e.target as Node)) {
        const isButtonClick = Object.values(stepMenuButtonRefs.current).some(btn => btn && btn.contains(e.target as Node))
        if (!isButtonClick) {
          setStepMenuOpen(null)
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDeleteField = async (stepId: number, fieldId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}/field/${fieldId}`)
      setFieldsInSteps(prev => prev.filter(field => !(field.pipelineField.id === fieldId && field.step.id === stepId)))
    } catch (error) {
      console.error(error)
    }
  }

  const moveStep = (sourceStepId: number, targetStepId: number) => {
    const sourceIndex = steps.findIndex(step => step.id === sourceStepId)
    const targetIndex = steps.findIndex(step => step.id === targetStepId)
    if(sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) return
    const updated = [...steps]
    const [removed] = updated.splice(sourceIndex, 1)
    updated.splice(targetIndex, 0, removed)
    const positioned = updated.map((step, idx) => ({ ...step, position: idx }))
    setSteps(positioned)
    saveStepOrder(positioned)
  }


  const handleTicketMove = async (ticketId: number, newStepId: number) => {
    try {
      const requiredFieldsResponse = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/required-fields/${newStepId}`
      )
      
      if (requiredFieldsResponse.data?.data && !requiredFieldsResponse.data.data.allFilled) {
        setRequiredFieldsModal({ ticketId, targetStepId: newStepId })
        return
      }

      const sourceStepId = tickets.find((t) => t.id === ticketId)?.stepId
      const targetTickets = tickets.filter((t) => t.stepId === newStepId && t.id !== ticketId)
      const newPosition = targetTickets.length
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        stepId: newStepId,
      })
      if(response.data) {
        setTickets(prev => {
          const moved = prev.find((t) => t.id === ticketId)
          const withoutMoved = prev.filter((t) => t.id !== ticketId)
          const targetList = withoutMoved.filter((t) => t.stepId === newStepId)
          const sourceList = sourceStepId ? withoutMoved.filter((t) => t.stepId === sourceStepId) : []
          const newTarget = [...targetList, { ...(moved || response.data.data), stepId: newStepId, position: newPosition }]
          const reorderedTarget = newTarget.map((t, idx) => ({ ...t, position: idx }))
          const reorderedSource = sourceStepId && sourceStepId !== newStepId
            ? sourceList.map((t, idx) => ({ ...t, position: idx }))
            : sourceList
          const others = withoutMoved.filter((t) => t.stepId !== newStepId && (!sourceStepId || t.stepId !== sourceStepId))
          if (sourceStepId && sourceStepId !== newStepId && reorderedSource.length) {
            saveTicketOrder(sourceStepId, reorderedSource)
          }
          saveTicketOrder(newStepId, reorderedTarget)
          return [...others, ...reorderedSource, ...reorderedTarget]
        })
      }
    } catch (error) {
      console.error('Erro ao mover ticket:', error)
    }
  }

  const saveTicketTitle = async (ticketId: number) => {
    const newTitle = ticketTitleDraft.trim()
    if(!newTitle) return
    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        title: newTitle
      })
      if(response.data) {
        setTickets(prev => prev.map(ticket => ticket.id === ticketId ? {...ticket, title: response.data.data.title} : ticket))
        setEditingTicketId(null)
        setTicketTitleDraft('')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const cancelEditTicket = () => {
    setEditingTicketId(null)
    setTicketTitleDraft('')
  }

  const saveFieldName = async (fieldId: number) => {
    const trimmed = fieldNameDraft.trim()
    if(!trimmed) return
    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/${fieldId}`, {
        name: trimmed
      })
      if(response.data) {
        setFieldsInSteps((prev) => prev.map((item) => item.pipelineField.id === fieldId ? { ...item, pipelineField: { ...item.pipelineField, name: response.data.data.name } } : item))
        setEditingFieldId(null)
        setFieldNameDraft('')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const cancelEditField = () => {
    setEditingFieldId(null)
    setFieldNameDraft('')
  }

  const handleDeleteTicket = async (ticketId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`)
      setTickets(prev => prev.filter(ticket => ticket.id !== ticketId))
      if(editingTicketId === ticketId) {
        cancelEditTicket()
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleDuplicateTicket = async (ticketId: number) => {
    const ticket = tickets.find(t => t.id === ticketId)
    if (!ticket) return
    try {
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket`, {
        title: `${ticket.title} (cópia)`,
        stepId: ticket.stepId
      })
      if (response.data) {
        setTickets(prev => [...prev, {...response.data.data, comments:[], tags:[]}])
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleCopyLink = (ticketId: number) => {
    const url = `${window.location.origin}/panel/pipeline/ticket?ticketId=${ticketId}`
    navigator.clipboard.writeText(url)
    alert('Link copiado para a área de transferência')
  }

  const handleArchiveTicket = async (ticketId: number) => {
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/archive`)
      setTickets(prev => prev.filter(ticket => ticket.id !== ticketId))
    } catch (error) {
      console.error('Erro ao arquivar ticket:', error)
    }
  }

  const handleUnarchiveTicket = async (ticketId: number) => {
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/unarchive`)
      setTickets(prev => prev.filter(ticket => ticket.id !== ticketId))
    } catch (error) {
      console.error('Erro ao desarquivar ticket:', error)
    }
  }

  const handleRenameFromTable = (ticketId: number) => {
    setEditingTicketId(ticketId)
    const ticket = tickets.find(t => t.id === ticketId)
    if (ticket) {
      setTicketTitleDraft(ticket.title)
    }
  }

  const handleCreateTableTicket = async (title: string, stepId: number) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket`, {
        title,
        stepId
      })
      if(response.data) {
        setTickets(prev => [...prev, {...response.data.data, comments:[], tags:[], tasks:[], fileInTickets:[]}])
      }
    } catch (error) {
      console.error(error)
    }
  }

  if (viewMode === 'table') {
    return (
      <>
        <TableView
          tickets={filteredAndSortedTickets.map(t => ({ ...t, comments: t.comments ?? [], tags: t.tags ?? [], tasks: t.tasks ?? [], fileInTickets: t.fileInTickets ?? [] }))}
          steps={steps}
          onView={(id) => navigate(`/panel/pipeline/ticket?ticketId=${id}`)}
          onDuplicate={handleDuplicateTicket}
          onRename={handleRenameFromTable}
          onCopyLink={handleCopyLink}
          onArchive={showArchived ? handleUnarchiveTicket : handleArchiveTicket}
          onDelete={handleDeleteTicket}
          showArchived={showArchived}
          isAddingCard={isAddingTableCard}
          onCancelAddCard={() => setIsAddingTableCard(false)}
          onCreate={handleCreateTableTicket}
          editingTicketId={editingTicketId}
          titleDraft={ticketTitleDraft}
          onTitleDraftChange={setTicketTitleDraft}
          onSaveTitle={saveTicketTitle}
          onCancelEdit={cancelEditTicket}
        />
        {viewOrEditTagsModalIsOpen && <ViewOrEditTags closeModal={() => setViewOrEditTagsModalIsOpen(false)} />}
      </>
    )
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Pipeline>
        {steps.map(step => (
          <DroppableStepColumn 
            key={step.id} 
            step={step} 
            onTicketMove={handleTicketMove}
            onStepMove={moveStep}
          >
            <StepHeader>
              <TitleWithActions>
                {editingStepId === step.id ? (
                  <InputForAdd
                    value={stepNameDraft}
                    placeholder='Nome da etapa'
                    onChange={(e) => setStepNameDraft(e.target.value)}
                    onKeyDown={(e) => handleUpdateStep(step.id, e)}
                    autoFocus
                  />
                ) : (
                  <CardTitle>{step.name}</CardTitle>
                )}
                {editingStepId === step.id ? (
                  <ActionGroup>
                    <IconButton onClick={() => saveStepName(step.id)}><Check size={16} /></IconButton>
                    <IconButton onClick={cancelEditStep}><X size={16} /></IconButton>
                  </ActionGroup>
                ) : (
                  <StepSettingsButton 
                    ref={el => stepMenuButtonRefs.current[step.id] = el}
                    onClick={() => handleToggleStepMenu(step.id)}
                  >
                    <Settings size={16} />
                  </StepSettingsButton>
                )}
              </TitleWithActions>
            </StepHeader>
            <TicketList>
              {isConfiguringPipeline ? <>
                <AddInStep onClick={() => setFieldModal({ mode: 'selector', stepId: step.id })}><Plus />Adicionar campo</AddInStep>
                {fieldsInSteps.filter((field) => field.step?.id === step.id).map((field) => {
                  const isEditingField = editingFieldId === field.pipelineField.id
                  const isIntegrated = !!field.pipelineField.integrationConfig
                  const typeId = field.pipelineField.typeField?.id || (field.pipelineField as any).typeFieldId
                  
                  let typeLabel = ''
                  if (isIntegrated) {
                    const integrationType = field.pipelineField.integrationConfig?.integrationType === 'write' ? 'registro' : 'leitura'
                    const isRequired = field.isRequired || field.pipelineField.integrationConfig?.isRequired
                    typeLabel = `integrado - tipo ${integrationType}${isRequired ? ' - obrigatório' : ''}`
                  } else {
                    const typeName = field.pipelineField.typeField?.name || (typeId ? typeMap[typeId] : '') || ''
                    typeLabel = typeName + (field.isRequired ? ' - obrigatório' : '')
                  }

                  return (
                    <TicketItem key={field.pipelineField.id}>
                      <TitleWithActions>
                        {isEditingField ? (
                          <InputForAdd
                            value={fieldNameDraft}
                            placeholder='Nome do campo'
                            onChange={(e) => setFieldNameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if(e.key === 'Enter') saveFieldName(field.pipelineField.id)
                              if(e.key === 'Escape') cancelEditField()
                            }}
                            autoFocus
                          />
                        ) : (
                          <CardTitle>
                            {field.pipelineField.name} {typeLabel ? <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>({typeLabel})</span> : null}
                          </CardTitle>
                        )}
                        <ActionGroup>
                          {isEditingField ? (
                            <>
                              <IconButton onClick={() => saveFieldName(field.pipelineField.id)} onDoubleClick={(e) => e.stopPropagation()}><Check size={16} /></IconButton>
                              <IconButton onClick={cancelEditField} onDoubleClick={(e) => e.stopPropagation()}><X size={16} /></IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton onClick={() => {
                                setEditingFieldId(field.pipelineField.id)
                                setFieldNameDraft(field.pipelineField.name)
                              }} onDoubleClick={(e) => e.stopPropagation()}><SquarePen size={16} /></IconButton>
                              <IconButton onClick={() => handleDeleteField(step.id, field.pipelineField.id)} onDoubleClick={(e) => e.stopPropagation()}><Trash2 size={16} /></IconButton>
                            </>
                          )}
                        </ActionGroup>
                      </TitleWithActions>
                    </TicketItem>
                  )
                })}
              </> 
              : <>
                {filteredAndSortedTickets
                  .filter((ticket) => ticket.stepId === step.id)
                  .map((ticket, idx) => 
                  <DraggableTicket 
                    key={ticket.id} 
                    ticket={ticket} 
                    index={idx}
                    onOpen={() => navigate(`/panel/pipeline/ticket?ticketId=${ticket.id}`)}
                    onEditClick={() => {
                      setEditingTicketId(ticket.id)
                      setTicketTitleDraft(ticket.title)
                    }}
                    isEditing={editingTicketId === ticket.id}
                    titleDraft={ticketTitleDraft}
                    onChangeTitle={setTicketTitleDraft}
                    onConfirmEdit={() => saveTicketTitle(ticket.id)}
                    onCancelEdit={cancelEditTicket}
                    onDelete={() => handleDeleteTicket(ticket.id)}
                    onDuplicate={() => handleDuplicateTicket(ticket.id)}
                    onCopyLink={() => handleCopyLink(ticket.id)}
                    onArchive={() => showArchived ? handleUnarchiveTicket(ticket.id) : handleArchiveTicket(ticket.id)}
                    showArchived={showArchived}
                  />
                )}
              </>}
              {newTicketIn === step.id && (
                <TicketItem>
                  <HorizontalDiv style={{backgroundColor: '#F4F4F4', paddingRight: '1rem'}}>
                    <InputForAdd 
                      value={newTicketTitle} 
                      onChange={(e) => setNewTicketTitle(e.target.value)} 
                      placeholder='Título do card' 
                      onKeyDown={handleCreateTicket}
                      autoFocus
                      onBlur={createTicket}
                    /> <X color='#696969' size={20} style={{cursor: 'pointer'}} onClick={() => setNewTicketIn(null)} onDoubleClick={(e) => e.stopPropagation()} />
                  </HorizontalDiv>
                </TicketItem>
              )}
              {!isConfiguringPipeline && (step as any).allowCardCreation !== false && <AddInStep onClick={() => setNewTicketIn(step.id)}><Plus />Adicionar card</AddInStep>}
            </TicketList>
          </DroppableStepColumn>
        ))}
        <StepColumn><AddStep onClick={() => setAddStepModalIsOpen(true)}><Plus strokeWidth={1.5} /></AddStep></StepColumn>
      </Pipeline>

      {stepMenuOpen !== null && ReactDOM.createPortal(
        <StepSettingsMenu
          ref={stepMenuRef}
          top={stepMenuPosition.top}
          left={stepMenuPosition.left}
        >
          <StepSettingsMenuItem onClick={(e) => { e.stopPropagation(); handleToggleStepLock(stepMenuOpen) }}>
            <StepMenuItemLeft>
              <ToggleSwitch>
                <input 
                  type="checkbox" 
                  checked={(steps.find(s => s.id === stepMenuOpen) as any)?.allowCardCreation !== false} 
                  onChange={() => handleToggleStepLock(stepMenuOpen)}
                />
                <span />
              </ToggleSwitch>
              Adição de cards
            </StepMenuItemLeft>
          </StepSettingsMenuItem>
          <StepMenuDivider />
          <StepSettingsMenuItem onClick={() => handleDuplicateStep(stepMenuOpen)}>
            <StepMenuItemLeft>
              <Copy size={16} />
              Duplicar
            </StepMenuItemLeft>
          </StepSettingsMenuItem>
          <StepSettingsMenuItem onClick={() => handleStartRenameStep(stepMenuOpen)}>
            <StepMenuItemLeft>
              <Pencil size={16} />
              Renomear
            </StepMenuItemLeft>
          </StepSettingsMenuItem>
          <StepSettingsMenuItem $danger onClick={() => handleDeleteStep(stepMenuOpen)}>
            <StepMenuItemLeft>
              <Trash2 size={16} />
              Excluir
            </StepMenuItemLeft>
          </StepSettingsMenuItem>
        </StepSettingsMenu>,
        document.body
      )}

      {isConfiguringPipeline && fieldModal?.mode === 'selector' && (
        <FieldTypeSelector
          closeModal={() => setFieldModal(null)}
          onSelectCustomField={() => setFieldModal({ mode: 'custom', stepId: fieldModal.stepId })}
          onSelectIntegratedField={() => setFieldModal({ mode: 'integrated', stepId: fieldModal.stepId })}
        />
      )}

      {isConfiguringPipeline && fieldModal?.mode === 'custom' && (
        <AddFieldOnStep
          stepId={fieldModal.stepId}
          editingField={fieldModal.field}
          closeModal={() => {
            setFieldModal(null)
            fetchFieldsInStepsData()
          }}
          onBack={() => setFieldModal({ mode: 'selector', stepId: fieldModal.stepId })}
          onSaved={(updated) => {
            setFieldModal(null)
            if (updated) {
              setFieldsInSteps((prev) => prev.map((item) => item.pipelineField.id === updated.id ? { ...item, pipelineField: { ...item.pipelineField, name: updated.name } } : item))
            }
            fetchFieldsInStepsData()
          }}
        />
      )}

      {isConfiguringPipeline && fieldModal?.mode === 'integrated' && (
        <AddIntegratedFieldModal
          stepId={fieldModal.stepId}
          closeModal={() => setFieldModal(null)}
          onBack={() => setFieldModal({ mode: 'selector', stepId: fieldModal.stepId })}
          onSaved={() => {
            setFieldModal(null)
            fetchFieldsInStepsData()
          }}
        />
      )}
      {addStepModalIsOpen && <AddStepInPipeline pipelineId={Number(pipelineId)} closeModal={() => setAddStepModalIsOpen(false)} />}
      {viewOrEditTagsModalIsOpen && <ViewOrEditTags closeModal={() => setViewOrEditTagsModalIsOpen(false)} />}
      {requiredFieldsModal && (
        <FillRequiredFieldsModal
          ticketId={requiredFieldsModal.ticketId}
          targetStepId={requiredFieldsModal.targetStepId}
          onClose={() => setRequiredFieldsModal(null)}
          onSuccess={() => {
            setRequiredFieldsModal(null)
            fetchStepsAndTickets()
          }}
        />
      )}
    </DndProvider>
  )
}

export default Kanban